/**
 * VÉRAULT Date Formatting Utilities
 * Standard format: MM–DD–YYYY per spec
 * 
 * IMPORTANT: All date strings (YYYY-MM-DD) are treated as LOCAL dates,
 * not UTC. This prevents off-by-one day errors due to timezone conversion.
 */

/**
 * Parse a date string as a local date (not UTC)
 * This fixes the timezone offset issue where "2026-06-02" becomes June 1 in US timezones
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  // If it's a YYYY-MM-DD format, parse as local date components
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const parts = dateStr.split('T')[0].split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  // Fallback for other formats
  return new Date(dateStr);
}

/**
 * Format date to MM–DD–YYYY
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${month}–${day}–${year}`;
}

/**
 * Format date range: MM–DD to MM–DD–YYYY
 * @param {Date|string} startDate 
 * @param {Date|string} endDate 
 * @returns {string} Formatted date range
 */
export function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) return '';
  
  const start = typeof startDate === 'string' ? parseLocalDate(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseLocalDate(endDate) : endDate;
  
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return '';
  
  const startMonth = String(start.getMonth() + 1).padStart(2, '0');
  const startDay = String(start.getDate()).padStart(2, '0');
  const endMonth = String(end.getMonth() + 1).padStart(2, '0');
  const endDay = String(end.getDate()).padStart(2, '0');
  const endYear = end.getFullYear();
  
  // Same year: MM–DD to MM–DD–YYYY
  if (start.getFullYear() === end.getFullYear()) {
    return `${startMonth}–${startDay} to ${endMonth}–${endDay}–${endYear}`;
  }
  
  // Different years: MM–DD–YYYY to MM–DD–YYYY
  const startYear = start.getFullYear();
  return `${startMonth}–${startDay}–${startYear} to ${endMonth}–${endDay}–${endYear}`;
}

/**
 * Format date with weekday: Weekday, MM–DD–YYYY
 * @param {Date|string} date 
 * @returns {string}
 */
export function formatDateWithWeekday(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  return `${weekday}, ${formatDate(d)}`;
}

/**
 * Format date with short month name for display: Mon DD, YYYY
 * @param {Date|string} date 
 * @returns {string}
 */
export function formatDateReadable(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  const year = d.getFullYear();
  
  return `${month} ${day}, ${year}`;
}

/**
 * Format datetime: MM–DD–YYYY at HH:MM AM/PM
 * @param {Date|string} date 
 * @returns {string}
 */
export function formatDateTime(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  
  const time = d.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  return `${formatDate(d)} at ${time}`;
}

/**
 * Get short weekday from date
 * @param {Date|string} date 
 * @returns {string}
 */
export function getShortWeekday(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

/**
 * Get long weekday from date
 * @param {Date|string} date 
 * @returns {string}
 */
export function getLongWeekday(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

/**
 * ============================================================
 * SINGLE SOURCE OF TRUTH: Trip Date Accessors
 * ============================================================
 * These functions ensure all components read trip dates from
 * the same master fields. Use these instead of directly accessing
 * trip.start_date or trip.trip_start_date.
 * 
 * Master fields: trip_start_date, trip_end_date
 * Legacy fields (deprecated): start_date, end_date
 * ============================================================
 */

/**
 * Get the master start date from a trip object
 * Priority: trip_start_date > start_date
 * @param {Object} trip - Trip object
 * @returns {string} Date string in YYYY-MM-DD format or empty string
 */
export function getTripStartDate(trip) {
  if (!trip) return '';
  // Master field takes priority, fall back to legacy
  const date = trip.trip_start_date || trip.start_date || '';
  // Normalize to YYYY-MM-DD (strip time component if present)
  return date ? date.slice(0, 10) : '';
}

/**
 * Get the master end date from a trip object
 * Priority: trip_end_date > end_date
 * @param {Object} trip - Trip object
 * @returns {string} Date string in YYYY-MM-DD format or empty string
 */
export function getTripEndDate(trip) {
  if (!trip) return '';
  // Master field takes priority, fall back to legacy
  const date = trip.trip_end_date || trip.end_date || '';
  // Normalize to YYYY-MM-DD (strip time component if present)
  return date ? date.slice(0, 10) : '';
}

/**
 * Get both master dates from a trip object
 * @param {Object} trip - Trip object
 * @returns {Object} { startDate, endDate }
 */
export function getTripDates(trip) {
  return {
    startDate: getTripStartDate(trip),
    endDate: getTripEndDate(trip)
  };
}

/**
 * Get formatted trip date range using master fields
 * @param {Object} trip - Trip object
 * @returns {string} Formatted date range or empty string
 */
export function getTripDateRangeFormatted(trip) {
  const { startDate, endDate } = getTripDates(trip);
  return formatDateRange(startDate, endDate);
}

/**
 * Calculate total trip days from master fields
 * @param {Object} trip - Trip object
 * @returns {number} Number of days (inclusive) or 0
 */
export function getTripDayCount(trip) {
  const { startDate, endDate } = getTripDates(trip);
  if (!startDate || !endDate) return 0;
  
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive
  return diffDays;
}

/**
 * Get the primary destination from a trip object
 * Checks destination_segments first, falls back to destinations string
 * @param {Object} trip - Trip object
 * @returns {string} Primary destination name or empty string
 */
export function getTripPrimaryDestination(trip) {
  if (!trip) return '';
  
  // Check destination_segments array first
  if (trip.destination_segments && trip.destination_segments.length > 0) {
    const firstSegment = trip.destination_segments[0];
    if (firstSegment.destination_name) {
      return firstSegment.destination_name;
    }
  }
  
  // Fall back to legacy destinations string (take first if comma-separated)
  if (trip.destinations) {
    return trip.destinations.split(',')[0].trim();
  }
  
  return '';
}

/**
 * Get all destinations as a formatted string
 * @param {Object} trip - Trip object
 * @returns {string} Destinations joined with " → " or empty string
 */
export function getTripDestinationsDisplay(trip) {
  if (!trip) return '';
  
  // Check destination_segments array first
  if (trip.destination_segments && trip.destination_segments.length > 0) {
    const names = trip.destination_segments
      .map(s => s.destination_name)
      .filter(Boolean);
    if (names.length > 0) {
      return names.join(' → ');
    }
  }
  
  // Fall back to legacy destinations string
  return trip.destinations || '';
}

/**
 * Check if destination should be hidden (for client portal)
 * @param {Object} trip - Trip object
 * @returns {boolean} True if destination should be masked
 */
export function isDestinationHidden(trip) {
  if (!trip) return false;
  return trip.reveal_destination === 'masked' || trip.reveal_destination === 'hidden';
}

/**
 * Get destination display respecting visibility rules
 * @param {Object} trip - Trip object
 * @param {boolean} isClientView - Whether this is a client-facing view
 * @returns {string} Destination to display (masked or real)
 */
export function getDestinationDisplay(trip, isClientView = false) {
  if (!trip) return '';
  
  // Client view respects reveal settings
  if (isClientView && isDestinationHidden(trip)) {
    return trip.reveal_destination_mask || 'A surprise awaits ✨';
  }
  
  return getTripDestinationsDisplay(trip);
}

/**
 * Check if dates should be hidden (for client portal)
 * @param {Object} trip - Trip object
 * @returns {boolean} True if dates should be masked
 */
export function areDatesHidden(trip) {
  if (!trip) return false;
  return trip.reveal_dates === 'masked' || trip.reveal_dates === 'hidden';
}

/**
 * Get dates display respecting visibility rules
 * @param {Object} trip - Trip object
 * @param {boolean} isClientView - Whether this is a client-facing view
 * @returns {string} Dates to display (masked or real)
 */
export function getDatesDisplay(trip, isClientView = false) {
  if (!trip) return '';
  
  // Client view respects reveal settings
  if (isClientView && areDatesHidden(trip)) {
    return trip.reveal_dates_mask || '';
  }
  
  return getTripDateRangeFormatted(trip);
}

/**
 * Format a day label given the trip start date and day number
 * @param {Object|string} tripOrStartDate - Trip object or start date string
 * @param {number} dayNumber - 1-based day number
 * @returns {string} Formatted day label (e.g., "Monday, 06–15–2026")
 */
export function formatDayLabel(tripOrStartDate, dayNumber) {
  let startDate;
  
  if (typeof tripOrStartDate === 'object' && tripOrStartDate !== null) {
    startDate = getTripStartDate(tripOrStartDate);
  } else {
    startDate = tripOrStartDate;
  }
  
  if (!startDate || !dayNumber) return '';
  
  const start = parseLocalDate(startDate);
  if (!start || isNaN(start.getTime())) return '';
  
  const dayDate = new Date(start);
  dayDate.setDate(start.getDate() + (dayNumber - 1));
  
  return formatDateWithWeekday(dayDate);
}

export default {
  formatDate,
  formatDateRange,
  formatDateWithWeekday,
  formatDateReadable,
  formatDateTime,
  getShortWeekday,
  getLongWeekday,
  // Single source of truth functions
  getTripStartDate,
  getTripEndDate,
  getTripDates,
  getTripDateRangeFormatted,
  getTripDayCount,
  getTripPrimaryDestination,
  getTripDestinationsDisplay,
  isDestinationHidden,
  getDestinationDisplay,
  areDatesHidden,
  getDatesDisplay,
  formatDayLabel
};
